import { actions, actionTypes, selectors } from 'data'
import { OrderType } from 'data/Order/types'
import isEmpty from 'lodash/isEmpty'
import { call, fork, put, select, takeLeading } from 'redux-saga/effects'
import { getFilledTrades } from 'services/api/fills'
import { historical } from 'services/api/order'
import webSocket from 'services/api/webSocket'
import { Response } from 'utils/fetchApi'

import historicalPositionsMain from './positions/sagas'

const A_DAY = 1000 * 60 * 60 * 24
const DAYS_WINDOW = 90

type HistoryResponse = Response<{ readonly orders: Array<OrderType> }>
const getOrders = function* () {
  yield put(actions.historical.setIsLoadingOrders(true))
  const stateOrders = yield select(selectors.historical.getOrders)
  // This is important! We fetch a time window from last fetched order
  let before = !isEmpty(stateOrders)
    ? stateOrders[stateOrders.length - 1].transactionTime
    : +new Date()

  // eslint-disable-next-line prefer-const
  let data: HistoryResponse = yield call(historical, before)
  if (!data.ok) {
    yield put(actions.historical.setIsLoadingOrders(false))
    return
  }
  // FIXME: We have no proper API support for pagination,
  // so we can fetch but can't really know if there is more
  // To patch this, the first time we fetch and if get an empty response we
  // fetch again. We do this until we reach 3 fetches. Then we declare it empty
  let { orders } = data.response
  let totalFetches = 1
  while (orders.length === 0 && totalFetches < 3) {
    before -= DAYS_WINDOW * A_DAY
    data = yield call(historical, before)
    if (!data.ok) {
      yield put(actions.historical.setIsLoadingOrders(false))
      return
    }
    orders = data.response.orders
    totalFetches += 1
  }

  if (totalFetches === 3) {
    yield put(actions.historical.setCanLoadMore(false))
  }

  // Note: Backend said passing a limit of x (example 50) can return
  // anything below the limit, so to make sure we can load more
  // data, I am going to add this if statement check.
  const canLoadMore = yield select(selectors.historical.getCanLoadMore)
  if ((orders.length < 1 && canLoadMore) || (orders.length >= 1 && !canLoadMore)) {
    yield put(actions.historical.setCanLoadMore(!canLoadMore))
  }
  yield put(actions.historical.setOrders(stateOrders.concat(orders)))
  yield put(actions.historical.setIsLoadingOrders(false))
}

const getFilledTradesSaga = function* (action) {
  yield put(actions.historical.setIsLoadingTrades(true))
  const days = action.payload.days || 1
  const limit = action.payload.limit || 1000
  const data = yield call(getFilledTrades, days, limit)

  if (!data.ok) {
    yield put(actions.historical.setIsLoadingTrades(false))
    return
  }

  yield put(actions.historical.setFilledTrades(data.response.orders))
  yield put(actions.historical.setIsLoadingTrades(false))
}

const getFilledOrdersSaga = function* (action) {
  yield put(actions.historical.setIsLoadingOrders(true))
  const days = action.payload.days || 1
  const data: Response<{
    readonly orders: Array<OrderType>
  }> = yield call(historical, Date.now(), days, 1000)

  const liveOrders: Array<OrderType> = yield select(selectors.order.getLivePartials)
  // fill should include cancelled orders too, since we get either 'CANCELED' or
  // 'CANCELLED' we are going to check for both
  if (data.ok) {
    yield put(actions.historical.setFilledOrders([...data.response.orders, ...liveOrders]))
  }

  yield put(actions.historical.setIsLoadingOrders(false))
}

export default function* historicalMain() {
  yield takeLeading(actionTypes.historical.GET_ORDERS, getOrders)
  yield takeLeading(actionTypes.historical.GET_FILLED_ORDERS, getFilledOrdersSaga)
  yield takeLeading(actionTypes.historical.GET_FILLED_TRADES, getFilledTradesSaga)
  yield fork(historicalPositionsMain)
}
